/* ─────────────────────────────────────────────────────────────────────────────
   Primitivos de UI del apartado de Configuración.

   Antes cada pestaña repetía los mismos estilos inline (inputs, cajas
   punteadas, toggles, sliders) con variantes mínimas, y de ahí venía que dos
   pantallas de la misma sección se vieran distintas. Aquí vive una sola
   versión de cada pieza y todas las secciones la usan.
   ───────────────────────────────────────────────────────────────────────── */
import React, { useState } from 'react';
import { Plus, X } from 'lucide-react';

/* ─── Tokens ─────────────────────────────────────────────────────────────── */
export const T = {
  radio: 10,
  radioChico: 8,
  borde: 'var(--line-strong)',
  bordeSuave: 'var(--line)',
  texto: 'var(--ink)',
  texto2: 'var(--ink)',
  tenue: 'var(--muted)',
  debil: 'var(--muted)',
  marca: 'var(--brand)',        // fondo de botones
  marcaTexto: 'var(--brand-texto)', // texto, bordes y contornos (se aclara en oscuro)
  tarjeta: 'var(--card)',
};

export const inputStyle: React.CSSProperties = {
  width: '100%',
  border: `1px solid ${T.borde}`,
  borderRadius: T.radio,
  height: 48,
  padding: '0 14px',
  fontSize: 16,
  fontFamily: 'inherit',
  color: T.texto,
  background: 'var(--surface)',
  outline: 'none',
  boxSizing: 'border-box',
};

export const enfocar = (e: React.FocusEvent<HTMLElement>) => {
  e.currentTarget.style.borderColor = T.marcaTexto;
  e.currentTarget.style.boxShadow = '0 0 0 3px color-mix(in srgb, var(--brand-texto) 22%, transparent)';
};
export const desenfocar = (e: React.FocusEvent<HTMLElement>) => {
  e.currentTarget.style.borderColor = T.borde;
  e.currentTarget.style.boxShadow = 'none';
};

/* ─── Layout de una sección ──────────────────────────────────────────────── */
export function Seccion({ children }: { children: React.ReactNode }) {
  return <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>{children}</div>;
}

/** Bloque con título propio. Es el contenedor estándar de todo ajuste. */
export function Tarjeta({ titulo, descripcion, acciones, children }: {
  titulo?: string;
  descripcion?: string;
  acciones?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section style={{
      background: 'var(--surface)',
      border: `1px solid ${T.bordeSuave}`,
      borderRadius: 16,
      padding: 22,
      display: 'flex',
      flexDirection: 'column',
      gap: 14,
    }}>
      {(titulo || acciones) && (
        <header style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            {titulo && <h3 style={{ fontSize: 18, fontWeight: 700, color: T.texto, margin: 0 }}>{titulo}</h3>}
            {descripcion && <p style={{ fontSize: 15, color: T.tenue, margin: '4px 0 0', lineHeight: 1.5 }}>{descripcion}</p>}
          </div>
          {acciones}
        </header>
      )}
      {!titulo && descripcion && (
        <p style={{ fontSize: 15, color: T.tenue, margin: 0, lineHeight: 1.5 }}>{descripcion}</p>
      )}
      {children}
    </section>
  );
}

/** Rejilla de campos: 1 o 2 columnas según se pida. */
export function Rejilla({ cols = 2, children }: { cols?: 1 | 2; children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: cols === 2 ? 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))' : '1fr', gap: 14 }}>
      {children}
    </div>
  );
}

export function Campo({ label, hint, children }: { label?: string; hint?: string; children: React.ReactNode }) {
  return (
    <div style={{ minWidth: 0 }}>
      {label && <label style={{ fontSize: 14, fontWeight: 700, color: T.tenue, display: 'block', marginBottom: 6 }}>{label}</label>}
      {children}
      {hint && <p style={{ fontSize: 13, color: T.debil, margin: '5px 0 0', lineHeight: 1.45 }}>{hint}</p>}
    </div>
  );
}

/* ─── Controles ──────────────────────────────────────────────────────────── */
export function Entrada(props: React.InputHTMLAttributes<HTMLInputElement>) {
  const { style, ...rest } = props;
  return <input {...rest} style={{ ...inputStyle, ...style }} onFocus={enfocar} onBlur={desenfocar} />;
}

export function Selector(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  const { style, ...rest } = props;
  return <select {...rest} style={{ ...inputStyle, cursor: 'pointer', ...style }} onFocus={enfocar} onBlur={desenfocar} />;
}

export function Toggle({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const on = value !== 'false';
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={() => onChange(on ? 'false' : 'true')}
      style={{
        width: 52, height: 30, borderRadius: 999, transition: 'background .18s',
        flexShrink: 0, border: 'none', cursor: 'pointer', padding: 0,
        background: on ? 'var(--ok)' : 'var(--line-strong)', position: 'relative',
      }}
    >
      <div style={{
        width: 24, height: 24, background: '#fff', borderRadius: '50%',
        boxShadow: '0 1px 2px rgba(0,0,0,.25)', transition: 'left .18s',
        position: 'absolute', top: 3, left: on ? 25 : 3,
      }} />
    </button>
  );
}

/** Fila etiqueta + descripción + interruptor. Se apila sin separadores manuales. */
export function FilaToggle({ label, desc, cfgKey, config, actualizar, porDefecto = 'true' }: {
  label: string; desc?: string; cfgKey: string;
  config: any; actualizar: (k: string, v: string) => void; porDefecto?: string;
}) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
      padding: '14px 0', borderTop: `1px solid ${T.bordeSuave}`,
    }}>
      <div style={{ minWidth: 0 }}>
        <p style={{ fontSize: 16, fontWeight: 700, color: T.texto2, margin: 0 }}>{label}</p>
        {desc && <p style={{ fontSize: 14, color: T.debil, margin: '3px 0 0', lineHeight: 1.45 }}>{desc}</p>}
      </div>
      <Toggle value={config[cfgKey] ?? porDefecto} onChange={v => actualizar(cfgKey, v)} />
    </div>
  );
}

/** Contenedor de interruptores: recorta el margen del primer separador. */
export function ListaToggles({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', marginTop: -10 }}>
      {children}
    </div>
  );
}

/** Selector de opción única en forma de botones pegados. */
/* Campo de tiempo con selector seg/min. La clave guarda SIEMPRE en su unidad
   original (`guardadoEn`) para no migrar datos: aquí solo se convierte al mostrar
   y al escribir. Los minutos pueden quedar con decimales (45 seg → 0.75 min). */
export function EntradaTiempo({ valor, guardadoEn, onChange, min = 0 }: {
  valor: string | undefined;
  guardadoEn: 'seg' | 'min';
  onChange: (v: string) => void;
  min?: number;   // en la unidad guardada
}) {
  const [unidad, setUnidad] = React.useState<'seg' | 'min'>(guardadoEn);
  const factor = unidad === guardadoEn ? 1 : unidad === 'seg' ? 60 : 1 / 60;   // guardado → mostrado
  const n = parseFloat(valor ?? '');
  const mostrado = isNaN(n) ? '' : String(Math.round(n * factor * 100) / 100);
  const escribir = (texto: string) => {
    if (texto === '') return onChange('');
    const g = parseFloat(texto) / factor;
    if (isNaN(g)) return;
    onChange(String(guardadoEn === 'seg' ? Math.round(g) : Math.round(g * 100) / 100));
  };
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      <Entrada type="number" min={Math.round(min * factor * 100) / 100} step="any" value={mostrado}
        onChange={e => escribir(e.target.value)} style={{ flex: 1, minWidth: 0 }} />
      <Selector value={unidad} onChange={e => setUnidad(e.target.value as 'seg' | 'min')} style={{ width: 84, flexShrink: 0 }}>
        <option value="seg">seg</option>
        <option value="min">min</option>
      </Selector>
    </div>
  );
}

export function Segmentado({ value, onChange, opciones }: {
  value: string;
  onChange: (v: string) => void;
  opciones: { valor: string; label: string; hint?: string }[];
}) {
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {opciones.map(o => {
        const sel = value === o.valor;
        return (
          <button key={o.valor} type="button" onClick={() => onChange(o.valor)}
            style={{
              flex: '1 1 140px', textAlign: 'left', padding: '10px 14px', borderRadius: T.radio,
              cursor: 'pointer', transition: 'all .12s', fontFamily: 'inherit',
              border: `${sel ? 2 : 1}px solid ${sel ? T.marcaTexto : T.borde}`,
              background: sel ? 'var(--well)' : 'var(--surface)',
              color: T.texto,
            }}>
            <div style={{ fontSize: 16, fontWeight: 700 }}>{o.label}</div>
            {o.hint && <div style={{ fontSize: 13, color: T.tenue, marginTop: 2 }}>{o.hint}</div>}
          </button>
        );
      })}
    </div>
  );
}

export function Deslizador({ label, valor, onChange, min, max, step = 1, sufijo = '', izq, der }: {
  label: string; valor: number; onChange: (v: string) => void;
  min: number; max: number; step?: number; sufijo?: string; izq?: string; der?: string;
}) {
  return (
    <div>
      <label style={{ fontSize: 14, fontWeight: 700, color: T.tenue, display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        <span>{label}</span>
        <span style={{ fontWeight: 600, color: T.texto }}>{valor}{sufijo}</span>
      </label>
      <input type="range" min={min} max={max} step={step} value={valor}
        onChange={e => onChange(e.target.value)}
        style={{ width: '100%', accentColor: T.marcaTexto }} />
      {(izq || der) && (
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: T.debil, marginTop: 2 }}>
          <span>{izq}</span><span>{der}</span>
        </div>
      )}
    </div>
  );
}

/* ─── Botones ────────────────────────────────────────────────────────────── */
const botonBase: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
  height: 44, padding: '0 16px', borderRadius: T.radio,
  fontSize: 15, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer',
  border: '1px solid transparent', whiteSpace: 'nowrap',
};

export function BotonPrimario({ children, disabled, style, ...rest }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button {...rest} disabled={disabled}
      style={{ ...botonBase, background: disabled ? 'var(--line)' : T.marca, color: disabled ? 'var(--muted)' : 'var(--on-brand)', cursor: disabled ? 'default' : 'pointer', ...style }}>
      {children}
    </button>
  );
}

export function BotonSecundario({ children, style, ...rest }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button {...rest}
      style={{ ...botonBase, background: 'var(--surface)', border: `1px solid ${T.borde}`, color: T.texto, ...style }}>
      {children}
    </button>
  );
}

export function BotonPeligro({ children, style, ...rest }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button {...rest}
      style={{ ...botonBase, background: 'var(--surface)', border: '1px solid var(--danger-line)', color: 'var(--danger)', ...style }}>
      {children}
    </button>
  );
}

export function BotonIcono({ children, style, ...rest }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button {...rest}
      style={{
        width: 40, height: 40, border: 'none', background: 'transparent', color: T.debil,
        borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        ...style,
      }}
      onMouseEnter={e => { e.currentTarget.style.background = 'var(--well)'; e.currentTarget.style.color = T.texto2; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = T.debil; }}>
      {children}
    </button>
  );
}

/* ─── Avisos ─────────────────────────────────────────────────────────────── */
export function Aviso({ tono = 'info', children }: { tono?: 'info' | 'alerta' | 'ok'; children: React.ReactNode }) {
  const paleta = {
    info:   { bg: 'var(--well)', bd: 'var(--well)', tx: T.tenue },
    alerta: { bg: 'var(--warn-bg)', bd: 'var(--warn-bg)', tx: 'var(--warn)' },
    ok:     { bg: 'var(--ok-bg)', bd: 'var(--ok-bg)', tx: 'var(--ok)' },
  }[tono];
  return (
    <p style={{
      fontSize: 14, color: paleta.tx, background: paleta.bg,
      border: `1px solid ${paleta.bd}`, borderRadius: T.radioChico,
      padding: '10px 14px', margin: 0, lineHeight: 1.5,
    }}>
      {children}
    </p>
  );
}

/* ─── Lista de valores en chips (catálogos CSV) ──────────────────────────── */
export function ListaChips({ items, onAdd, onRemove, placeholder = 'Agregar valor…' }: {
  items: string[]; onAdd: (v: string) => void; onRemove: (v: string) => void; placeholder?: string;
}) {
  const [nuevo, setNuevo] = useState('');
  const agregar = () => {
    const v = nuevo.trim();
    if (!v || items.includes(v)) return;
    onAdd(v);
    setNuevo('');
  };
  return (
    <div>
      {items.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
          {items.map(v => (
            <span key={v} style={{
              display: 'flex', alignItems: 'center', gap: 2, fontSize: 14, fontWeight: 700,
              padding: '4px 4px 4px 12px', borderRadius: 999, background: 'var(--well)', color: T.texto2,
            }}>
              {v}
              <BotonIcono onClick={() => onRemove(v)} title={`Quitar ${v}`} aria-label={`Quitar ${v}`} style={{ width: 28, height: 28 }}>
                <X size={11} />
              </BotonIcono>
            </span>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <Entrada value={nuevo} onChange={e => setNuevo(e.target.value)} placeholder={placeholder}
          onKeyDown={e => e.key === 'Enter' && agregar()} />
        <BotonPrimario onClick={agregar} disabled={!nuevo.trim()} style={{ padding: '0 14px' }} title="Agregar">
          <Plus size={15} />
        </BotonPrimario>
      </div>
    </div>
  );
}

/* ─── Estado vacío ───────────────────────────────────────────────────────── */
export function Vacio({ children }: { children: React.ReactNode }) {
  return (
    <p style={{
      fontSize: 15, color: T.debil, margin: 0, padding: '18px 0',
      textAlign: 'center', border: `1px dashed ${T.borde}`, borderRadius: T.radio,
    }}>
      {children}
    </p>
  );
}
